import { Auth0Provider } from "@auth0/auth0-react";
import { useContext } from "react";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import { getMyAccountAudience, getMyAccountScope } from "utilities/auth0MyAccount/constants";

/**
 * Scoped Auth0 provider for the My Account MFA island. Deliberately isolated:
 * `useRefreshTokens={false}` + `cacheLocation="memory"` mean the elevated token is never persisted
 * and dies with the context, which — together with the 10-minute `max_age` prime in useMyAccountMfa
 * — enforces the elevated-access boundary for MFA editing.
 *
 * NON-NEGOTIABLE: this island must NEVER ride a future app-wide <Auth0Provider> that carries
 * refresh tokens. A silently-renewed shared session would defeat the 10-minute boundary. Any root
 * provider added later must leave this island on its own isolated provider (design: Isolation
 * permanence / Key Risks).
 */
export const Auth0MyAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const appDefaults = useContext(AppDefaultInfoContext);
    // The generated DTO types these as nullable; Auth0Provider requires strings.
    const domain = appDefaults!.coreSpaConfiguration.domain ?? "";
    const clientId = appDefaults!.coreSpaConfiguration.clientId ?? "";

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            useRefreshTokens={false}
            cacheLocation="memory"
            authorizationParams={{
                audience: getMyAccountAudience(domain),
                scope: getMyAccountScope(),
            }}
        >
            {children}
        </Auth0Provider>
    );
};
